import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {pluralize} from '~/common/utils';
import {useDeletePhoto} from '~/ViewInvitation/hooks/useDeletePhoto';
import {useFeaturePhoto} from '~/ViewInvitation/hooks/useFeaturePhoto';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {
  CLEAR_PENDING_PHOTO,
  CLEAR_PENDING_PHOTOS,
  NAME_HOST_GALLERY,
} from '~/ViewInvitation/reducers/constants';
import {
  PhotoContextMenu,
  PhotoDeleteAllConfirmationModal,
  PhotoDeleteConfirmationModal,
  PhotoFeatureConfirmationModal,
} from '~/ViewInvitation/routes/Photos/modals';
import {PhotoGalleryEditorFooter} from '~/ViewInvitation/routes/Photos/PhotoGalleryEditor/PhotoGalleryEditorFooter';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import {selectFailedPhotosCount, selectHostGalleryName} from '~/ViewInvitation/selectors/photos';
import {isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {ChevronsDownIcon} from '~library/svg/ChevronsDownIcon';
import {PhotoGalleryEditorActions} from './PhotoGalleryEditorActions';
import {PhotoGalleryEditorList} from './PhotoGalleryEditorList';

import './PhotoGalleryEditor.sass';

const ABOVE_FOLD_PHOTO_COUNT = 16;

// TODO: refactor into smaller sub-components
export const PhotoGalleryEditor = ({
  isOpen,
  onClose,
  stagedPhotos = [],
  isHostModule = false,
  collection = 'photos',
  handlePost,
  doFeature,
  onViewPhotoFn,
  isPosting,
  setPhotoGalleryOpen,
  onEditGallery,
  preview = isPreview(),
  setHasUploadedNoTitle = () => undefined,
}) => {
  const dispatch = useDispatch();
  const listRef = useRef(null);
  const fileUploadRef = useRef(null);
  const isMediumAndUp = useIsMediumAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const failedPhotoCount = useSelector(selectFailedPhotosCount);
  const eventId = useSelector(selectEventId);
  const [hideModal, setHideModal] = useState(false);
  const galleryName = useSelector(selectHostGalleryName);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showingContextMenu, setShowingContextMenu] = useState(false);
  const [showingDeleteMessage, setShowingDeleteMessage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showingDeleteAllMessage, setShowingDeleteAllMessage] = useState(false);
  const [newIndicatorHidden, setNewIndicatorHidden] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setDeleting(false);
      setHideModal(false);
    }
  }, [isOpen]);

  const feedback =
    failedPhotoCount > 0 ? `${pluralize('photo', failedPhotoCount)} failed to upload` : null;

  const {
    photos,
    title: savedGalleryTitle,
    isLoading,
  } = usePhotos({
    eventId,
    isHostModule,
    perPage: -1,
    includeCount: true,
  });

  const combinedPhotos = useMemo(() => {
    if (!isHostModule) return [...stagedPhotos];
    const newArr = [...photos, ...stagedPhotos];

    const idx = newArr.findIndex((item) => item.position === 1);
    if (idx > 0) {
      const [obj] = newArr.splice(idx, 1);
      newArr.unshift(obj);
    }
    return newArr;
  }, [photos, stagedPhotos, isHostModule]);

  const photoCount = combinedPhotos?.length;

  const {deleteCurrentPhoto, deleteAllPhotos} = useDeletePhoto({
    photos,
    photoCount,
    setShowingDeleteMessage,
    setShowingContextMenu,
    isHostModule,
    setHasUploadedNoTitle,
  });

  const {
    photoToFeature,
    showingFeatureMessage,
    handleFeaturePhoto,
    confirmFeaturePhoto,
    handleCloseFeatureConfirm,
  } = useFeaturePhoto({
    isHostModule,
    doFeature,
    setPhotoToDelete,
    setShowingContextMenu,
  });

  const onGalleryNameChange = useCallback(
    (value) => {
      dispatch({type: NAME_HOST_GALLERY, payload: value});
    },
    [dispatch]
  );

  const handleDeletePhoto = useCallback((id) => {
    setPhotoToDelete(id);
    setShowingDeleteMessage(true);
  }, []);

  const handleDeleteAllPhotos = useCallback(() => {
    setShowingDeleteAllMessage(true);
  }, []);

  const handleSave = useCallback(() => {
    setSubmitted(true);
    handlePost();
    setHideModal(true);
  }, [handlePost]);

  const deletePhoto = useCallback(async () => {
    setDeleting(true);
    // If photo is saved, DELETE
    if (photos.some((obj) => obj.id === photoToDelete)) {
      await deleteCurrentPhoto(null, photoToDelete);
      // Else clear from store
    } else {
      dispatch({
        type: CLEAR_PENDING_PHOTO,
        payload: {
          id: photoToDelete,
          collection,
        },
      });
    }
    setShowingDeleteMessage(false);
    setShowingContextMenu(false);
    setDeleting(false);
    setPhotoToDelete(null);
  }, [dispatch, photos, collection, photoToDelete, deleteCurrentPhoto]);

  const doDeleteAllPhotos = useCallback(async () => {
    if (isHostModule) {
      await deleteAllPhotos();
    }
    dispatch({type: CLEAR_PENDING_PHOTOS});
    setShowingDeleteAllMessage(false);
  }, [dispatch, deleteAllPhotos, isHostModule]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    if (stagedPhotos.length > 0) {
      setNewIndicatorHidden(false);
    }
  }, [stagedPhotos]);

  const spinner = isPosting ? <span className="evt-spinner inline no-margin" /> : null;
  const primaryActionText = (
    <>
      {spinner} {isHostModule ? 'Save' : 'Post photos'}
    </>
  );

  const showNewIndicator =
    combinedPhotos?.length > ABOVE_FOLD_PHOTO_COUNT && stagedPhotos?.length > 0 && isHostModule;

  return (
    <>
      <ResponsivePanel
        title={isHostModule ? 'Edit gallery' : 'Post photos'}
        portalMode
        showCover
        modalId="photo-editor-modal"
        isOpen={isOpen && !hideModal}
        onClose={onClose}
        renderMobileViewAs="bottom-sheet"
        disableOverlayClick
      >
        <div
          className={cx('photo-editor__wrapper', {
            'photo-editor__wrapper--disabled': submitted,
          })}
        >
          {isHostModule && (
            <Input
              className="gallery-name-input"
              label="Gallery Name"
              data-qa-id="gallery-name"
              type="text"
              value={galleryName || savedGalleryTitle || ''}
              onChange={(e) => onGalleryNameChange(e.target.value)}
              placeholder="e.g. Our Engagement Photos"
              maxLength={60}
            />
          )}
          <div className="photo-editor__content">
            <div className="photo-editor__content__header">
              <Typography variant="paragraph1" semibold>
                Photos ({photoCount})
              </Typography>
              <PhotoGalleryEditorActions
                combinedPhotosLength={combinedPhotos.length}
                isLargishAndUp={isLargishAndUp}
                isLoading={isLoading}
                handleDeleteAllPhotos={handleDeleteAllPhotos}
                fileUploadRef={fileUploadRef}
                isHostModule={isHostModule}
              />
            </div>
            <PhotoGalleryEditorList
              combinedPhotos={combinedPhotos}
              isLoading={isLoading}
              isHostModule={isHostModule}
              onViewPhotoFn={onViewPhotoFn}
              handleFeaturePhoto={handleFeaturePhoto}
              handleDeletePhoto={handleDeletePhoto}
              fileUploadRef={fileUploadRef}
              collection={collection}
              photoCount={photoCount}
              isMediumAndUp={isMediumAndUp}
              listRef={listRef}
            />
            {showNewIndicator && (
              <Button
                className={cx('photo-gallery__new-indicator', {
                  'photo-gallery__new-indicator--hidden': newIndicatorHidden,
                })}
                variant="blurred"
                data-qa-id="photo-gallery-new-indicator"
                aria-label="New photos added"
                onClick={scrollToBottom}
                icon={<ChevronsDownIcon />}
              >
                <Typography variant="paragraph3">New</Typography>
              </Button>
            )}
          </div>
          <PhotoGalleryUploadInput
            fileUploadRef={fileUploadRef}
            collection={collection}
            setPhotoGalleryOpen={setPhotoGalleryOpen}
            onEditGallery={onEditGallery}
          />
        </div>
        <PhotoGalleryEditorFooter
          onClose={onClose}
          handleSave={handleSave}
          primaryActionText={primaryActionText}
          isCreationFlow={preview}
          feedback={feedback}
        />
      </ResponsivePanel>

      {/** MODALS */}
      {showingFeatureMessage && (
        <PhotoFeatureConfirmationModal
          onApprove={confirmFeaturePhoto}
          onClose={handleCloseFeatureConfirm}
        />
      )}
      {!isLargishAndUp && showingContextMenu && (
        <PhotoContextMenu
          photo={photoToFeature}
          onFeaturePhoto={confirmFeaturePhoto}
          onDeletePhoto={deletePhoto}
          isHostModule={isHostModule}
          deleting={deleting}
          onClose={() => {
            setShowingContextMenu(false);
            setPhotoToDelete(null);
          }}
        />
      )}
      {showingDeleteMessage && (
        <PhotoDeleteConfirmationModal
          onApprove={deletePhoto}
          onClose={() => {
            setPhotoToDelete(null);
            setShowingDeleteMessage(false);
          }}
          isHostModule={isHostModule}
        />
      )}
      {showingDeleteAllMessage && (
        <PhotoDeleteAllConfirmationModal
          onApprove={doDeleteAllPhotos}
          onClose={() => setShowingDeleteAllMessage(false)}
          photoCount={photoCount}
          isHostModule={isHostModule}
        />
      )}
    </>
  );
};
